/**
 * primitiveBlocks.tsx — Componentes web dos primitivos e da seção com camadas
 *
 * Usam exatamente as mesmas classes do compilador HTML (`st-*`, definidas em
 * `studioLayers.css` e espelhadas em `layerStyles()` do compilador), para que a
 * prévia do editor e a página publicada sejam idênticas.
 *
 * RELEASE: v1.3.0 - 2026-09-11 - Faixa de borda a borda (`bleed`) e player de
 *            vídeo com capa, botão de play e link normal (YouTube/Vimeo/Loom)
 * RELEASE: v1.2.0 - 2026-09-11 - Recorte, inclinação e forma decorativa (Fase 6)
 * RELEASE: v1.1.0 - 2026-09-11 - Chave "Camadas livres" (.st-layers) — Fase 3
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, motor de modelos — Fase 1)
 */

import React from 'react';
import type { StudioWebBlockComponent } from './types';
import { resolveAssetUrl } from '@/lib/studio/core/assets/stockCatalog';
import './studioLayers.css';
import { criarOrcamentoDeMovimento } from '@/lib/studio/core/motion';
import { classeDaForma, classesDoVisual, estiloDoVisual } from '@/lib/studio/core/visual';
import { lerVideo } from '@/lib/studio/video/embed';
import { tracadosDoIcone } from '@/lib/studio/core/icons';

/** Lê uma propriedade como texto. */
function texto(valor: unknown): string {
  return typeof valor === 'string' ? valor : valor == null ? '' : String(valor);
}

/** Lê uma propriedade de imagem resolvendo referências do acervo (`acervo://`). */
function imagemUrl(valor: unknown): string {
  return resolveAssetUrl(texto(valor));
}

/** Quebra o conteúdo em linhas e cada linha em partes separadas por "|". */
function linhas(valor: unknown): string[][] {
  return texto(valor)
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.split('|').map((parte) => parte.trim()));
}

/**
 * Orçamento de interação do editor (Fase 5).
 * O editor usa o mesmo registro e o mesmo teto da página publicada, para que o
 * que se vê na tela de criação seja o que vai ao ar. O teto é renovado sempre
 * que a primeira seção do documento é desenhada.
 */
let ORCAMENTO_MOVIMENTO = criarOrcamentoDeMovimento('editor');

/** Renova o orçamento (chamado ao desenhar a raiz do documento). */
export function reiniciarOrcamentoDeMovimento(): void {
  ORCAMENTO_MOVIMENTO = criarOrcamentoDeMovimento('editor');
}

/** Classes de movimento do bloco (vazia quando "nenhum" ou fora do orçamento). */
function movimento(props: unknown): string {
  return ORCAMENTO_MOVIMENTO.classes(props);
}

/** Classes de acabamento visual (recorte e inclinação) no modo editor. */
function visual(props: unknown): string {
  return classesDoVisual(props, 'editor');
}

/** Estilo em linha do acabamento visual (traçado do desenho próprio). */
function visualStyle(props: unknown): React.CSSProperties {
  const declaracao = estiloDoVisual(props, 'editor');
  if (!declaracao) return {};
  const [nome, valor] = declaracao.replace(/;$/, '').split(/:(.+)/);
  return { [nome]: valor } as React.CSSProperties;
}

/** Junta classes ignorando vazios. */
function cls(...partes: Array<string | false | undefined>): string {
  return partes.filter(Boolean).join(' ');
}

// =============================================================================
// Seção com camadas
// =============================================================================

export const LayeredSectionBlock: StudioWebBlockComponent = ({ node, children }) => {
  const p = node.props;
  const gradiente = texto(p.backgroundGradient);
  const imagem = imagemUrl(p.backgroundImage);
  const overlay = texto(p.overlay);
  const padrao = texto(p.pattern);
  const brilho = texto(p.glow);
  const altura = texto(p.minHeight) || 'auto';
  const disposicao = texto(p.layout) || 'stack';

  return (
    <section
      className={cls(
        'st-sec',
        // Faixa: sem escolha gravada, vai de borda a borda (é o formato das
        // páginas profissionais). "Com margem lateral" volta ao miolo.
        texto(p.bleed) === 'contained' && 'st-contained',
        `st-r-${texto(p.radius) || 'none'}`,
        altura !== 'auto' && `st-h-${altura}`,
        movimento(p),
      )}
      style={{
        backgroundColor: texto(p.backgroundColor) || undefined,
        color: texto(p.textColor) || undefined,
      }}
    >
      {gradiente && gradiente !== 'none' ? <div className={`st-bg st-gr-${gradiente}`} /> : null}
      {imagem ? <div className="st-bg" style={{ backgroundImage: `url(${imagem})` }} /> : null}
      {overlay && overlay !== 'none' ? <div className={`st-ov st-ov-${overlay}`} /> : null}
      {padrao && padrao !== 'none' ? <div className={`st-pat st-pat-${padrao}`} /> : null}
      {brilho && brilho !== 'none' ? <div className={`st-glow st-glow-${brilho}`} /> : null}

      <div
        className={cls(
          'st-inner',
          `st-pad-${texto(p.paddingY) || 'lg'}`,
          disposicao !== 'stack' && `st-lay-${disposicao}`,
          // Libera o posicionamento ancorado dos filhos (guarda-corpo da Fase 3)
          p.allowLayers === true && 'st-layers',
          `st-al-${texto(p.align) || 'left'}`,
        )}
        style={{ maxWidth: texto(p.maxWidth) || '1120px' }}
      >
        {children}
      </div>
    </section>
  );
};

// =============================================================================
// Primitivos
// =============================================================================

/**
 * Contêiner vertical: agrupa vários blocos dentro de uma coluna.
 * Mesmas classes do compilador HTML.
 */
export const StackBlock: StudioWebBlockComponent = ({ node, children }) => {
  const p = node.props;
  return (
    <div
      className={cls(
        'st-stack',
        `st-stack-gap-${texto(p.gap) || 'md'}`,
        `st-stack-al-${texto(p.align) || 'start'}`,
        `st-stack-jt-${texto(p.justify) || 'start'}`,
        `st-stack-pad-${texto(p.padding) || 'none'}`,
        `st-r-${texto(p.radius) || 'none'}`,
        texto(p.border) && texto(p.border) !== 'none' && `st-stack-bd-${texto(p.border)}`,
        p.sticky === true && 'st-stack-sticky',
        movimento(p),
      )}
      style={{
        maxWidth: texto(p.maxWidth) || undefined,
        backgroundColor: texto(p.backgroundColor) || undefined,
        color: texto(p.textColor) || undefined,
      }}
    >
      {children}
    </div>
  );
};

export const BadgeBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const alinhamento = texto(p.align) || 'left';
  const variante = texto(p.variant) || 'soft';
  return (
    <div style={{ textAlign: alinhamento as React.CSSProperties['textAlign'] }}>
      <span
        className={cls(`st-badge st-badge-${variante}`, movimento(p))}
        style={
          texto(p.accentColor)
            ? variante === 'solid'
              ? { backgroundColor: texto(p.accentColor) }
              : { color: texto(p.accentColor) }
            : undefined
        }
      >
        {/* Estilo editorial: um traço curto na cor de destaque abre o chapéu. */}
        {variante === 'line' ? <span className="st-badge-rule" aria-hidden /> : null}
        {p.prefix ? <span aria-hidden>{texto(p.prefix)}</span> : null}
        {texto(p.text)}
      </span>
    </div>
  );
};

export const TitleBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const Tag = (texto(p.level) || 'h1') as 'h1' | 'h2' | 'h3';
  const conteudo = texto(p.text);
  const destaque = texto(p.highlight);
  const cor = texto(p.accentColor) || undefined;
  const caixa = texto(p.transform) || 'inherit';
  const peso = texto(p.weight) || 'inherit';

  const partes =
    destaque && conteudo.includes(destaque)
      ? conteudo.split(destaque)
      : null;

  return (
    <Tag
      className={cls(
        `st-title st-title-${texto(p.size) || 'xl'}`,
        `st-al-${texto(p.align) || 'left'}`,
        caixa !== 'inherit' && `st-tt-${caixa}`,
        `st-lead-${texto(p.leading) || 'normal'}`,
        movimento(p),
      )}
      style={{
        color: texto(p.color) || undefined,
        fontWeight: peso !== 'inherit' ? (peso as React.CSSProperties['fontWeight']) : undefined,
        maxWidth: texto(p.maxWidth) || undefined,
        // Respeitar as quebras digitadas é o que permite título de capa em
        // três linhas exatas, como nas páginas editoriais.
        whiteSpace: p.preserveLineBreaks === true ? 'pre-line' : undefined,
      }}
    >
      {partes ? (
        <>
          {partes[0]}
          <span style={{ color: cor }}>{destaque}</span>
          {partes.slice(1).join(destaque)}
        </>
      ) : (
        conteudo
      )}
    </Tag>
  );
};

export const ParagraphBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const alinhamento = texto(p.align) || 'left';
  return (
    <div
      className={cls(`st-p st-p-${texto(p.size) || 'md'}`, `st-al-${alinhamento}`, movimento(p))}
      style={{
        color: texto(p.color) || undefined,
        maxWidth: texto(p.maxWidth) || undefined,
        marginLeft: alinhamento === 'center' ? 'auto' : undefined,
        marginRight: alinhamento === 'center' ? 'auto' : undefined,
      }}
      // Conteúdo já sanitizado ao salvar/publicar o modelo
      dangerouslySetInnerHTML={{ __html: texto(p.text) }}
    />
  );
};

export const ButtonsBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const alinhamento = texto(p.align) || 'left';
  const tamanho = texto(p.size) || 'md';
  const cor = texto(p.accentColor);
  const forma = texto(p.shape) || 'rounded';
  return (
    <div
      className={cls(
        'st-btns',
        alinhamento === 'center' && 'st-btns-center',
        alinhamento === 'right' && 'st-btns-right',
        movimento(p),
      )}
    >
      {linhas(p.items).map(([rotulo, destino, estilo], indice) => (
        <a
          key={`${rotulo}-${indice}`}
          href={destino || '#'}
          className={cls(
            `st-btn st-btn-${tamanho} st-btn-${estilo || 'solid'}`,
            `st-btn-shape-${forma}`,
            p.uppercase === true && 'st-btn-caps',
          )}
          style={cor && (estilo || 'solid') === 'solid' ? { backgroundColor: cor } : undefined}
        >
          {rotulo}
          {p.arrow === true ? <span className="st-btn-arrow" aria-hidden>→</span> : null}
        </a>
      ))}
    </div>
  );
};

export const ImagePrimitiveBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const proporcao = texto(p.ratio) || 'auto';
  return (
    <img
      src={imagemUrl(p.src)}
      alt={texto(p.alt)}
      loading="lazy"
      className={cls(
        'st-img',
        proporcao !== 'auto' && `st-ratio-${proporcao}`,
        `st-r-${texto(p.radius) || 'lg'}`,
        `st-sh-${texto(p.shadow) || 'md'}`,
        visual(p),
        movimento(p),
      )}
      style={{ width: texto(p.width) || '100%', ...visualStyle(p) }}
    />
  );
};

/** Ícone do registro do sistema (nunca desenho vindo do documento). */
function IconeDoSistema({ nome }: { nome: unknown }) {
  const tracados = tracadosDoIcone(nome);
  if (!tracados.length) return null;
  return (
    <svg
      className="st-icon"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {tracados.map((d, indice) => (
        <path key={indice} d={d} />
      ))}
    </svg>
  );
}

export const CardBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const temIcone = tracadosDoIcone(p.iconName).length > 0;
  const altura = texto(p.minHeight) || 'auto';
  return (
    <div
      className={cls(
        `st-card st-card-${texto(p.variant) || 'solid'}`,
        `st-r-${texto(p.radius) || 'lg'}`,
        `st-sh-${texto(p.shadow) || 'md'}`,
        p.highlighted === true && 'st-card-hl',
        p.hover === true && 'st-card-hover',
        altura !== 'auto' && `st-card-h-${altura}`,
        visual(p),
        movimento(p),
      )}
      style={{
        backgroundColor: texto(p.backgroundColor) || undefined,
        color: texto(p.textColor) || undefined,
        ...visualStyle(p),
      }}
    >
      {p.index ? <span className="st-card-index" aria-hidden>{texto(p.index)}</span> : null}
      {temIcone ? (
        <div className="st-card-badge" aria-hidden>
          <IconeDoSistema nome={p.iconName} />
        </div>
      ) : p.icon ? (
        <div className="st-card-icon" aria-hidden>{texto(p.icon)}</div>
      ) : null}
      {p.eyebrow ? <p className="st-card-eyebrow">{texto(p.eyebrow)}</p> : null}
      {p.title ? <p className="st-card-title">{texto(p.title)}</p> : null}
      {p.text ? <p className="st-card-text">{texto(p.text)}</p> : null}
    </div>
  );
};

/** Iniciais de um nome, usadas quando a pessoa não tem foto. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '•';
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
}

export const AvatarsBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const alinhamento = texto(p.align) || 'left';
  const itens = linhas(p.items);
  const limite = Math.max(1, Number(texto(p.max)) || 5);
  const visiveis = itens.slice(0, limite);
  const excedente = itens.length - visiveis.length;
  const nota = texto(p.rating);
  return (
    <div
      className={cls(
        'st-avatars',
        alinhamento === 'center' && 'st-avatars-center',
        alinhamento === 'right' && 'st-avatars-right',
        movimento(p),
      )}
    >
      {visiveis.length ? (
        <div className="st-avatars-row">
          {visiveis.map(([url, nome], indice) =>
            url ? (
              <img key={`${url}-${indice}`} src={resolveAssetUrl(url)} alt={nome || ''} loading="lazy" />
            ) : (
              // Sem foto, mostramos as iniciais: nunca uma imagem quebrada.
              <span key={`ini-${indice}`} className="st-avatar-ini" title={nome || undefined}>
                {iniciais(nome || '')}
              </span>
            ),
          )}
          {excedente > 0 ? <span className="st-avatar-ini st-avatar-more">{`+${excedente}`}</span> : null}
        </div>
      ) : null}
      <div>
        {nota ? (
          <div className="st-avatars-rating">
            <span aria-hidden>★★★★★</span>
            <strong>{nota}</strong>
          </div>
        ) : null}
        {p.text ? <div className="st-avatars-text">{texto(p.text)}</div> : null}
        {p.note ? <div className="st-avatars-note">{texto(p.note)}</div> : null}
      </div>
    </div>
  );
};


export const StatBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  return (
    <div className={cls(`st-al-${texto(p.align) || 'left'}`, movimento(p))}>
      <p className="st-stat-value" style={{ color: texto(p.accentColor) || undefined }}>
        {texto(p.value)}
      </p>
      <p className="st-stat-label">{texto(p.label)}</p>
    </div>
  );
};

export const LogosBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const alinhamento = texto(p.align) || 'left';
  return (
    <div
      className={cls(
        'st-logos',
        p.grayscale !== false && 'st-logos-gray',
        alinhamento === 'center' && 'st-logos-center',
        movimento(p),
      )}
    >
      {p.note ? <p className="st-logos-note">{texto(p.note)}</p> : null}
      {linhas(p.items).map(([url, nome], indice) => (
        <img key={`${url}-${indice}`} src={resolveAssetUrl(url)} alt={nome || ''} loading="lazy" />
      ))}
    </div>
  );
};

export const DeviceBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const aparelho = texto(p.device) || 'browser';
  const imagem = (
    <img src={imagemUrl(p.src)} alt={texto(p.alt)} loading="lazy" className="st-img" />
  );
  return (
    <div
      className={cls('st-dev', `st-sh-${texto(p.shadow) || 'lg'}`, `st-dev-${aparelho}`, visual(p), movimento(p))}
      style={visualStyle(p)}
    >
      {aparelho === 'browser' ? (
        <div className="st-dev-bar" aria-hidden>
          <span />
          <span />
          <span />
        </div>
      ) : null}
      {imagem}
      {aparelho === 'laptop' ? <div className="st-dev-base" aria-hidden /> : null}
    </div>
  );
};

/**
 * Vídeo — aceita o link normal do YouTube, Vimeo, Loom ou de um arquivo.
 * O serviço só é carregado depois do clique no play: a página abre leve e a
 * capa aparece na hora, sem retângulo preto.
 */
export const VideoPrimitiveBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const fonte = lerVideo(p.videoUrl);
  const formato = texto(p.aspect) || '16x9';
  const capa = imagemUrl(p.posterUrl) || fonte.posterUrl;
  const rotulo = p.playLabel == null ? 'Assistir' : texto(p.playLabel);
  const [tocando, setTocando] = React.useState(false);
  const classe = cls(
    'st-vid',
    `st-vid-${formato}`,
    `st-r-${texto(p.radius) || 'lg'}`,
    `st-sh-${texto(p.shadow) || 'lg'}`,
    visual(p),
    movimento(p),
  );

  if (tocando && fonte.fileUrl) {
    return (
      <div className={classe}>
        <video src={fonte.fileUrl} poster={capa || undefined} controls autoPlay playsInline />
      </div>
    );
  }
  if (tocando && fonte.embedUrl) {
    return (
      <div className={classe}>
        <iframe src={fonte.embedUrl} title="Vídeo" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }

  return (
    <div className={classe}>
      {capa ? <img className="st-vid-poster" src={capa} alt="" loading="lazy" /> : null}
      <div className="st-vid-shade" aria-hidden />
      {fonte.provider === 'none' ? (
        <div className="st-vid-empty">
          {fonte.originalUrl
            ? 'Não reconhecemos este endereço de vídeo. Cole o link do YouTube, Vimeo, Loom ou de um arquivo .mp4.'
            : 'Cole o endereço do vídeo nas propriedades deste bloco.'}
        </div>
      ) : (
        <>
          <button type="button" className="st-vid-play" aria-label="Reproduzir vídeo" onClick={() => setTocando(true)} />
          {rotulo ? <span className="st-vid-label">{rotulo}</span> : null}
        </>
      )}
    </div>
  );
};

/**
 * Forma decorativa (Fase 6) — enfeite de composição. Nunca recebe conteúdo e
 * não captura cliques; some na impressão.
 */
export const ShapeBlock: StudioWebBlockComponent = ({ node }) => {
  const p = node.props;
  const classe = classeDaForma(texto(p.shape), texto(p.size), 'editor');
  if (!classe) return null;
  return (
    <div
      aria-hidden
      className={cls(classe, movimento(p))}
      style={texto(p.color) ? ({ ['--st-shape']: texto(p.color) } as React.CSSProperties) : undefined}
    />
  );
};
